import Box from "@mui/material/Box";
import Tab, { TabTypeMap } from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Theme, useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { styled as muiStyled } from "@mui/system";
import clsx from "clsx";
import {
  AUCTION_STATUS_CANCELLED,
  AUCTION_STATUS_SUCCESS,
  PATIENT_TABS,
} from "core/consts";
import { isAuctionRunning } from "core/model/auctions";
import { isPrintMode } from "core/model/utils/urls";
import { Auction, Patient, TourElement } from "core/types";
import { DotWithBorder } from "ds_legacy/components/Dot";
import {
  BORDER_BOTTOM,
  DISABLED_COLOR,
  ERROR_COLOR,
  GREY_50,
  SUCCESS_COLOR,
  TEXT_STEPPER,
  WHITE,
} from "ds_legacy/materials/colors";
import { border, dp } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
} from "ds_legacy/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useScrollToTop } from "dsl/hooks";
import { useCareseekerNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import { useClearSearchParams } from "dsl/hooks/useRemoveQueryParam";
import { PRINT_PARAM_KEYS } from "dsl/molecules/Messenger/PrintMessenger";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslations } from "translations";
import { getDotProps, useGetPatientTabConfigs } from "./helpers";

export type PatientTab = (typeof PATIENT_TABS)[number];

export type PatientTabConfig = TabTypeMap["props"] & {
  hide?: boolean;
  tour?: TourElement;
};

const useStyles = makeStyles(() => ({
  tab: {
    height: "var(--tabs-height)",
    minHeight: "var(--tabs-height)",
  },
  tabs: {
    background: `${WHITE}`,
    borderBottom: border({ color: BORDER_BOTTOM }),
    boxSizing: "border-box",
    height: "var(--tabs-height)",
    minHeight: "var(--tabs-height)",
    position: "fixed",
    top: `${dp(160)}`,
    width: "100%",
    zIndex: 40,
  },
}));

function a11yProps(value: PatientTabConfig["value"]) {
  return {
    "aria-controls": `simple-tabpanel-${value}`,
    "data-testid": `${value}-button`,
    id: `patient-tab-${value}`,
  };
}

function getAuctionStatusColor(
  auction: Auction | null | undefined,
  theme: Theme,
) {
  const newAnswersNumber = auction?.new_responses;

  const status = auction?.status;

  if (isAuctionRunning(status)) {
    if (newAnswersNumber && newAnswersNumber > 0)
      return theme.palette.secondary.main;
    return theme.palette.primary.main;
  }

  switch (status) {
    case AUCTION_STATUS_CANCELLED:
      return ERROR_COLOR;
    case AUCTION_STATUS_SUCCESS:
      return SUCCESS_COLOR;
    default:
      return null;
  }
}

const StyledTabs = muiStyled(Tabs)(() => ({
  "& .MuiTabs-flexContainer": {
    alignItems: "center",
    height: "inherit",
    minHeight: "inherit",
  },
  "& .MuiTabs-scroller": {
    height: "inherit",
    minHeight: "inherit",
  },
  background: WHITE,
  borderBottom: border({ color: BORDER_BOTTOM }),
  boxSizing: "border-box",
  height: "var(--tabs-height)",
  minHeight: "var(--tabs-height)",
  position: "fixed",
  top: "calc(var(--navbar-height) + var(--patient-menu-height))",
  width: "100%",
  zIndex: 40,
}));

const StyledTab = muiStyled(Tab)<{ isActive?: boolean }>(({ isActive }) => ({
  "&> .MuiBox-root": {
    alignItems: "center",
    display: "flex",
    flexFlow: "row",
  },
  fontWeight: isActive ? FONT_WEIGHT_BOLD : FONT_WEIGHT_MEDIUM,
  height: "inherit",
  opacity: 1,
  textTransform: "capitalize",

  whiteSpace: "nowrap",
}));

export default function PatientNavigation({
  auction,
  children,
  patient,
}: {
  auction: Auction | undefined;
  children: React.ReactNode;
  patient: Patient;
}) {
  useScrollToTop();
  useClearSearchParams(["activeTab", "recommendation", ...PRINT_PARAM_KEYS]);
  const classes = useStyles();
  const navigationHandlers = useCareseekerNavigationHandlers();
  const theme = useTheme();
  const { isDesktop } = useMedia();
  const isPrint = isPrintMode();
  const patientId = patient.id;
  const params = useParams();
  const tabs = useGetPatientTabConfigs({ auction });
  const translations = useTranslations();

  const activeStep = useMemo(() => {
    const tabParam = params["*"]?.split("/")?.[0];
    return tabs.find(({ value }) => tabParam === value);
  }, [params["*"], tabs.length]);

  const { color } = useMemo(
    () => ({
      color: getAuctionStatusColor(auction, theme),
    }),
    [auction, theme],
  );

  if (!auction || isPrint) {
    return <>{children}</>;
  }

  const renderTab = (
    { disabled, hide, icon, label, tour, value }: PatientTabConfig,
    index: number,
  ) => {
    const isActive = value === activeStep?.value;

    const { dotColor, showDot } = getDotProps({
      color,
      disabled,
      value,
    });
    return !hide ? (
      <StyledTab
        key={index}
        className={classes.tab}
        disabled={disabled}
        label={
          <Box display="flex">
            <Body
              className={clsx({ active: isActive })}
              color={
                disabled
                  ? DISABLED_COLOR
                  : isActive === true
                  ? theme.palette.primary.main
                  : TEXT_STEPPER
              }
              fontWeight={isActive === true ? "bold" : "normal"}
              margin="unset"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {label}
            </Body>
            {icon}
            {showDot && <DotWithBorder color={dotColor || undefined} />}
          </Box>
        }
        value={value}
        onClick={() =>
          navigationHandlers.patient.goToPatientHomeTab({
            auctionId: auction?.id,
            patientId,
            tab: value,
          })
        }
        {...a11yProps(value)}
        {...tour}
      />
    ) : null;
  };

  return (
    <>
      <StyledTabs
        aria-label={translations.menu.patientMenu}
        indicatorColor="primary"
        scrollButtons={false}
        value={activeStep?.value}
        variant={!isDesktop ? "scrollable" : "fullWidth"}
      >
        {tabs.map((tab, index) => renderTab(tab, index))}
      </StyledTabs>
      <div
        style={{
          backgroundColor: GREY_50,
          height: "var(--tabs-height)",
        }}
      />
      {tabs.map(({ value }, index) => {
        const isActive = value === activeStep?.value;
        return isActive ? (
          <Box
            key={index}
            aria-labelledby={`patient-tab-${value}`}
            bgcolor={GREY_50}
            boxSizing="border-box"
            className="min-h-below-navbar-patient-menu-tabs"
            data-testid={`patient-tabpanel-${value}`}
            id={`patient-tabpanel-${value}`}
            maxWidth="100%"
            role="tabpanel"
          >
            {children}
          </Box>
        ) : null;
      })}
    </>
  );
}
